import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { updateBookingStatus } from '../services/bookingService';
import { sendBookingConfirmation, sendAccessDetails } from '../services/emailService';
import { useAuth } from '../contexts/AuthContext';

export default function BookingCompletePage() {
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const process = async () => {
      const redirectStatus = searchParams.get('redirect_status');
      const bookingId = searchParams.get('bookingId');

      if (redirectStatus === 'succeeded') {
        try {
          if (bookingId) {
            await updateBookingStatus(bookingId, 'confirmed');
          }
          await sendBookingConfirmation({ to: currentUser?.email, name: currentUser?.displayName }).catch(() => {});
          await sendAccessDetails({ to: currentUser?.email, name: currentUser?.displayName }).catch(() => {});
          setStatus('success');
          setMessage('Your payment was successful and your booking is confirmed!');
          toast.success('Booking confirmed!');
        } catch {
          setStatus('success');
          setMessage('Payment received! Your booking is confirmed.');
        }
      } else if (redirectStatus === 'processing') {
        setStatus('processing');
        setMessage('Your payment is being processed. We\'ll confirm your booking by email once it clears.');
      } else if (redirectStatus === 'requires_payment_method') {
        setStatus('failed');
        setMessage('Payment failed. Please try again or use a different payment method.');
      } else {
        // Direct navigation (e.g. after bank deposit) — show generic success
        setStatus('success');
        setMessage('Your booking has been submitted. We\'ll be in touch to confirm the details.');
      }
    };
    process();
  }, [searchParams, currentUser]);

  const icons = {
    loading: (
      <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
    ),
    success: (
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    ),
    processing: (
      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
        <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    ),
    failed: (
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    ),
  };

  const titles = {
    loading: 'Processing…',
    success: 'Booking Confirmed!',
    processing: 'Payment Processing',
    failed: 'Payment Failed',
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full card p-8 text-center space-y-6">
        {icons[status] ?? icons.loading}
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">{titles[status] ?? 'Processing…'}</h1>
          <p className="text-slate-500 text-sm mt-2">{message}</p>
        </div>

        {status === 'success' && (
          <div className="bg-brand-50 border border-brand-100 rounded-xl p-4 text-sm text-brand-800 text-left">
            <p className="font-semibold mb-1">Next steps:</p>
            <ul className="list-disc list-inside text-brand-700 space-y-1">
              <li>Check your email for a booking confirmation</li>
              <li>Access details will be sent within 1 hour</li>
            </ul>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/dashboard" className="btn-primary">My Dashboard</Link>
          {status === 'failed' && <Link to="/book" className="btn-secondary">Try Again</Link>}
        </div>
      </div>
    </div>
  );
}
